import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, UserCog, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "operator", "deliverer", "client"];
const EMPTY_FORM = { name: "", email: "", password: "", role: "operator", tenant_id: "", tenant_ids: [], operator_id: "", is_active: true };

function tr(t, key, vars = {}) {
  let s = t(key);
  for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return s;
}

function roleLabel(role, t) {
  return t(`role_${role}`) || role;
}

function RolePill({ role, t }) {
  const cls = {
    admin: "pill pill-info",
    operator: "pill pill-neutral",
    deliverer: "pill pill-warning",
    client: "pill pill-success",
  }[role] || "pill pill-neutral";
  return <span className={cls}>{roleLabel(role, t)}</span>;
}

function formComplete(form) {
  if (form.role === "admin") return true;
  if (form.role === "operator") return form.tenant_ids.length > 0;
  if (form.role === "deliverer") return Boolean(form.tenant_id && form.operator_id);
  return Boolean(form.tenant_id);
}

function UserFormFields({ form, setForm, tenants, operators, t, mode }) {
  const needsTenant = form.role !== "admin" && form.role !== "operator";
  const isOperator = form.role === "operator";
  const isDeliverer = form.role === "deliverer";
  const isEdit = mode === "edit";

  const onRoleChange = (v) => {
    setForm({
      ...form,
      role: v,
      tenant_id: v === "admin" || v === "operator" ? "" : form.tenant_id,
      tenant_ids: v === "operator" ? form.tenant_ids : [],
      operator_id: v === "deliverer" ? form.operator_id : "",
    });
  };

  const toggleTenant = (id) => {
    const next = form.tenant_ids.includes(id)
      ? form.tenant_ids.filter((x) => x !== id)
      : [...form.tenant_ids, id];
    setForm({ ...form, tenant_ids: next, tenant_id: next[0] || "" });
  };

  return (
    <>
      <div>
        <label className="text-xs font-medium text-[var(--text-2)]">{t("label_name")}</label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          className="rounded-xl h-10 mt-1"
          data-testid="user-input-name"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-[var(--text-2)]">{t("email")}</label>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          className="rounded-xl h-10 mt-1"
          data-testid="user-input-email"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-[var(--text-2)]">{t("password")}</label>
        <Input
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required={!isEdit}
          minLength={isEdit ? undefined : 6}
          placeholder={isEdit ? t("users_password_optional") : ""}
          className="rounded-xl h-10 mt-1"
          data-testid="user-input-password"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-[var(--text-2)]">{t("users_role")}</label>
        <Select value={form.role} onValueChange={onRoleChange}>
          <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="user-input-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => (
              <SelectItem key={r} value={r}>{roleLabel(r, t)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {isOperator && (
        <div>
          <label className="text-xs font-medium text-[var(--text-2)]">{t("users_tenants")}</label>
          <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-[var(--border-c)] bg-[var(--surface-2)] p-2 space-y-1" data-testid="user-input-tenants">
            {tenants.length === 0 && (
              <div className="text-xs text-[var(--text-3)] px-1 py-2">{t("users_select_tenants")}</div>
            )}
            {tenants.map((tn) => (
              <label key={tn.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-1 rounded-lg hover:bg-[var(--surface-hover)]">
                <Checkbox
                  checked={form.tenant_ids.includes(tn.id)}
                  onCheckedChange={() => toggleTenant(tn.id)}
                  data-testid={`user-tenant-${tn.id}`}
                />
                {tn.name}
              </label>
            ))}
          </div>
          <p className="text-[10px] text-[var(--text-3)] mt-1">{t("users_select_tenants")}</p>
        </div>
      )}
      {needsTenant && (
        <div>
          <label className="text-xs font-medium text-[var(--text-2)]">{t("nav_tenants")}</label>
          <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
            <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="user-input-tenant">
              <SelectValue placeholder={t("users_select_tenant")} />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((tn) => (
                <SelectItem key={tn.id} value={tn.id}>{tn.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {isDeliverer && (
        <div>
          <label className="text-xs font-medium text-[var(--text-2)]">{t("users_operator")}</label>
          <Select value={form.operator_id} onValueChange={(v) => setForm({ ...form, operator_id: v })}>
            <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="user-input-operator">
              <SelectValue placeholder={t("users_select_operator")} />
            </SelectTrigger>
            <SelectContent>
              {operators.map((op) => (
                <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {operators.length === 0 && (
            <p className="text-[10px] text-[var(--text-3)] mt-1">{t("users_no_operators")}</p>
          )}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox
          checked={form.is_active}
          onCheckedChange={(v) => setForm({ ...form, is_active: !!v })}
          data-testid="user-input-active"
        />
        {t("users_active")}
      </label>
    </>
  );
}

function UserFormDialog({ open, onOpenChange, mode, user, tenants, operators, t, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    if (isEdit && user) {
      const tenantIds = user.role === "operator"
        ? (user.tenant_ids?.length ? user.tenant_ids : (user.tenant_id ? [user.tenant_id] : []))
        : [];
      setForm({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "operator",
        tenant_id: user.tenant_id || "",
        tenant_ids: tenantIds,
        operator_id: user.operator_id || "",
        is_active: user.is_active !== false,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, isEdit, user]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        is_active: form.is_active,
        tenant_id: form.role === "admin" ? null : (form.role === "operator" ? (form.tenant_ids[0] || null) : form.tenant_id),
        tenant_ids: form.role === "operator" ? form.tenant_ids : [],
        operator_id: form.role === "deliverer" ? form.operator_id : null,
      };
      if (form.password) payload.password = form.password;

      if (isEdit) {
        await api.patch(`/users/${user.id}`, payload);
        toast.success(t("users_updated"));
      } else {
        if (!form.password) {
          toast.error(t("users_password_required"));
          setSaving(false);
          return;
        }
        await api.post("/users", { ...payload, password: form.password });
        toast.success(t("users_created"));
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (isEdit ? t("users_update_failed") : t("users_create_failed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid={isEdit ? "user-edit-dialog" : "user-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display font-bold">
            {isEdit ? t("users_edit") : t("users_new")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <UserFormFields form={form} setForm={setForm} tenants={tenants} operators={operators} t={t} mode={mode} />
          <Button
            type="submit"
            disabled={saving || !formComplete(form)}
            className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11"
            data-testid="user-submit"
          >
            {saving ? "..." : (isEdit ? t("users_save") : t("users_create_btn"))}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [bulkRole, setBulkRole] = useState("");
  const [bulkTenant, setBulkTenant] = useState("");
  const [bulkActive, setBulkActive] = useState("");
  const [busy, setBusy] = useState(false);

  const tenantMap = useMemo(
    () => Object.fromEntries(tenants.map((x) => [x.id, x.name])),
    [tenants],
  );

  const operators = useMemo(
    () => items.filter((u) => u.role === "operator"),
    [items],
  );

  const tenantNames = (u) => {
    const ids = u.role === "operator" && u.tenant_ids?.length
      ? u.tenant_ids
      : (u.tenant_id ? [u.tenant_id] : []);
    if (!ids.length) return "—";
    return ids.map((id) => tenantMap[id] || id.slice(0, 8)).join(", ");
  };

  const load = useCallback(async () => {
    const [{ data: users }, { data: tList }] = await Promise.all([
      api.get("/users"),
      api.get("/tenants"),
    ]);
    setItems(users || []);
    setTenants(tList || []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((u) => u.id)));
  };

  const applyBulk = async () => {
    if (!selected.size) return;
    const payload = { user_ids: [...selected] };
    if (bulkRole) payload.role = bulkRole;
    if (bulkTenant) payload.tenant_id = bulkTenant;
    if (bulkActive === "true") payload.is_active = true;
    if (bulkActive === "false") payload.is_active = false;
    if (!payload.role && payload.tenant_id === undefined && payload.is_active === undefined) {
      toast.error(t("users_bulk_nothing"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch("/users/bulk", payload);
      toast.success(tr(t, "users_bulk_updated", { count: data.updated }));
      setSelected(new Set());
      setBulkRole("");
      setBulkTenant("");
      setBulkActive("");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("users_bulk_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="card-soft px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--text-2)] font-medium">{t("section_governance")}</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5 flex items-center gap-2">
            <UsersIcon size={22} /> {t("nav_users")}
          </h1>
          <p className="text-xs text-[var(--text-2)] mt-1">{t("users_subtitle")}</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4"
          data-testid="new-user-btn"
        >
          <Plus size={14} className="mr-1" /> {t("users_new")}
        </Button>
      </div>

      {selected.size > 0 && (
        <div className="card-soft px-5 py-4 flex flex-wrap items-end gap-3 border-2 border-[var(--brand)]/30" data-testid="users-bulk-bar">
          <div className="text-sm font-semibold text-[var(--text-1)] w-full sm:w-auto">
            {tr(t, "users_selected", { count: selected.size })}
          </div>
          <div>
            <div className="text-xs mb-1 text-[var(--text-2)]">{t("users_role")}</div>
            <Select value={bulkRole} onValueChange={setBulkRole}>
              <SelectTrigger className="w-40 rounded-xl h-9" data-testid="bulk-role-select">
                <SelectValue placeholder={t("users_keep")} />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{roleLabel(r, t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs mb-1 text-[var(--text-2)]">{t("nav_tenants")}</div>
            <Select value={bulkTenant} onValueChange={setBulkTenant}>
              <SelectTrigger className="w-44 rounded-xl h-9" data-testid="bulk-tenant-select">
                <SelectValue placeholder={t("users_keep")} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((tn) => (
                  <SelectItem key={tn.id} value={tn.id}>{tn.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs mb-1 text-[var(--text-2)]">{t("users_status")}</div>
            <Select value={bulkActive} onValueChange={setBulkActive}>
              <SelectTrigger className="w-36 rounded-xl h-9" data-testid="bulk-active-select">
                <SelectValue placeholder={t("users_keep")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{t("users_active")}</SelectItem>
                <SelectItem value="false">{t("users_inactive")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={applyBulk}
            disabled={busy}
            className="rounded-xl h-9 bg-[var(--brand)] hover:bg-[var(--brand-hover)]"
            data-testid="bulk-apply"
          >
            <UserCog size={14} className="mr-1" /> {t("users_bulk_apply")}
          </Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())} className="rounded-xl h-9">
            {t("users_clear_selection")}
          </Button>
        </div>
      )}

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-c)] bg-[var(--surface-2)] text-left text-xs text-[var(--text-2)]">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={items.length > 0 && selected.size === items.length}
                    onCheckedChange={toggleAll}
                    data-testid="users-select-all"
                  />
                </th>
                <th className="px-4 py-3">{t("label_name")}</th>
                <th className="px-4 py-3">{t("email")}</th>
                <th className="px-4 py-3">{t("users_role")}</th>
                <th className="px-4 py-3">{t("nav_tenants")}</th>
                <th className="px-4 py-3">{t("users_operator")}</th>
                <th className="px-4 py-3">{t("users_status")}</th>
                <th className="px-4 py-3">{t("label_created")}</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-[var(--text-2)]">{t("users_empty")}</td>
                </tr>
              )}
              {items.map((u) => (
                <tr
                  key={u.id}
                  className={`border-b border-[var(--border-c)] last:border-0 hover:bg-[var(--surface-2)]/60 ${selected.has(u.id) ? "bg-[var(--brand-soft)]/40" : ""}`}
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selected.has(u.id)}
                      onCheckedChange={() => toggle(u.id)}
                      data-testid={`user-check-${u.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-[var(--text-1)]">
                    {u.name}
                    {u.id === currentUser?.id && (
                      <span className="ml-2 text-[10px] text-[var(--text-3)]">({t("users_you")})</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-2)]">{u.email}</td>
                  <td className="px-4 py-3"><RolePill role={u.role} t={t} /></td>
                  <td className="px-4 py-3 text-xs text-[var(--text-2)]">{tenantNames(u)}</td>
                  <td className="px-4 py-3 text-xs text-[var(--text-2)]">
                    {u.role === "deliverer" ? (u.operator_name || "—") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`pill ${u.is_active !== false ? "pill-success" : "pill-neutral"}`}>
                      {u.is_active !== false ? t("users_active") : t("users_inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--text-2)] font-mono">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setEditUser(u)}
                      className="w-8 h-8 rounded-lg hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] flex items-center justify-center text-[var(--text-2)]"
                      data-testid={`user-edit-${u.id}`}
                      title={t("users_edit")}
                    >
                      <Pencil size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UserFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        tenants={tenants}
        operators={operators}
        t={t}
        onSaved={() => { setCreateOpen(false); load(); }}
      />

      <UserFormDialog
        open={!!editUser}
        onOpenChange={(open) => { if (!open) setEditUser(null); }}
        mode="edit"
        user={editUser}
        tenants={tenants}
        operators={operators.filter((op) => op.id !== editUser?.id)}
        t={t}
        onSaved={() => { setEditUser(null); load(); }}
      />
    </div>
  );
}
